import React, { useMemo, useState } from 'react';
import { ArrowLeft } from './Icons';
import { unitStudyingData } from '../data/unitStudyingData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const UnitStudyingPage = ({ onBack, onLessonClick, userPreferences }) => {
  const [selectedLesson, setSelectedLesson] = useState(null);
  
  const { displayLanguage } = userPreferences;

  const { data: remote } = useSupabaseData({
    table: 'eng_unitstudying',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const data = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return unitStudyingData;
    const rows = Array.isArray(remote) ? remote : [remote];
    const currentUnitRow = rows.find(r => r.data_type === 'current_unit');
    const lessonsRows = rows.filter(r => r.data_type === 'lesson');
    const out = {
      currentUnit: currentUnitRow ? {
        id: currentUnitRow.current_unit_id || currentUnitRow.id,
        title: { 
          tc: currentUnitRow.current_unit_title_tc || '', 
          sc: currentUnitRow.current_unit_title_sc || '', 
          en: currentUnitRow.current_unit_title_en || '' 
        },
        description: { 
          tc: currentUnitRow.current_unit_description_tc || '', 
          sc: currentUnitRow.current_unit_description_sc || '', 
          en: currentUnitRow.current_unit_description_en || '' 
        },
        progress: {
          completedLessons: currentUnitRow.current_unit_completed_lessons || 0,
          totalLessons: currentUnitRow.current_unit_total_lessons || lessonsRows.length
        }
      } : unitStudyingData.currentUnit,
      lessons: lessonsRows.length > 0 ? lessonsRows.map((r, idx) => ({
        id: r.lesson_id || r.id,
        lessonNumber: r.lesson_number || idx + 1,
        title: { 
          tc: r.lesson_title_tc || '', 
          sc: r.lesson_title_sc || '', 
          en: r.lesson_title_en || '' 
        },
        description: { 
          tc: r.lesson_description_tc || '', 
          sc: r.lesson_description_sc || '', 
          en: r.lesson_description_en || '' 
        },
        progress: r.lesson_progress || 0,
        duration: { 
          tc: r.lesson_duration_tc || '1 週', 
          sc: r.lesson_duration_sc || '1 周', 
          en: r.lesson_duration_en || '1 week' 
        },
        difficulty: r.lesson_difficulty || 'beginner',
        status: r.lesson_status || 'locked',
        isUnlocked: r.lesson_is_unlocked !== false
      })) : unitStudyingData.lessons,
      navigation: unitStudyingData.navigation
    };
    return out;
  }, [remote]);

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const handleLessonClick = (lesson) => {
    if (lesson.isUnlocked) {
      setSelectedLesson(lesson);
    }
  };

  const handleStartLesson = () => {
    if (selectedLesson) {
      console.log('Starting lesson:', selectedLesson.id);
      onLessonClick(selectedLesson.id);
      setSelectedLesson(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 pt-12 pb-4">
        {/* Top Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            <ArrowLeft size={24} />
          </button>
        </div>
      </div>

      {/* Unit Header */}
      <div className="px-6 mb-6 text-center">
        <h1 className="text-xl font-bold mb-1">UNIT 1</h1>
        <p className="text-sm text-gray-300 mb-2">
          {data.currentUnit.progress.completedLessons}/{data.currentUnit.progress.totalLessons} Lessons
        </p>
        <h2 className="text-lg font-bold mb-1">
          {getText(data.currentUnit.title)}
        </h2>
        <p className="text-sm text-gray-300">
          {getText(data.currentUnit.description)}
        </p>
      </div>

      {/* Lessons List */}
      <div className="px-6 mb-20">
        <div className="space-y-4">
          {data.lessons.map((lesson) => (
            <div
              key={lesson.id}
              onClick={() => handleLessonClick(lesson)}
              className={`bg-gray-800/30 border border-gray-600 rounded-xl p-4 cursor-pointer transition-all ${
                lesson.isUnlocked 
                  ? 'hover:bg-gray-700/50 hover:border-gray-500' 
                  : 'opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-4">
                {/* Lesson Number */}
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    {lesson.lessonNumber}
                  </div>
                </div>

                {/* Lesson Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white mb-1">
                    {getText(lesson.title)}
                  </h3>
                  <p className="text-gray-300 text-sm mb-2">
                    {getText(lesson.description)}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-700 rounded-full h-1 mb-2">
                    <div 
                      className="bg-green-500 h-1 rounded-full transition-all duration-300"
                      style={{ width: `${lesson.progress}%` }}
                    ></div>
                  </div>
                  
                  {/* Duration and Difficulty */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {getText(lesson.duration)}
                    </span>
                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded font-medium">
                      {lesson.difficulty.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="flex-shrink-0">
                  {lesson.status === 'active' ? (
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  ) : (
                    <div className="w-3 h-3 rounded-full border-2 border-gray-400"></div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lesson Start Modal */}
      {selectedLesson && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">
              {getText(selectedLesson.title)}
            </h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Description:</span>
                <span className="text-sm">{getText(selectedLesson.description)}</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Duration:</span>
                <span>{getText(selectedLesson.duration)}</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Difficulty:</span>
                <span className="capitalize">{selectedLesson.difficulty}</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Progress:</span>
                <span>{selectedLesson.progress}%</span>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedLesson(null)}
                className="flex-1 py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartLesson}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                {getText(data.navigation.startLessonButton)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitStudyingPage;
